import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Plus, Minus, Check, Truck, Package } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { getProduct, PRODUCTS } from "@/data/products";
import { useCart } from "@/context/CartContext";
import { Reveal } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";
import Stamp from "@/components/Stamp";
import Seo, { breadcrumbLd, productLd } from "@/components/Seo";
import { getProductSeo } from "@/lib/seo";

const ProductDetail = () => {
  const { id } = useParams();
  const { t, tr, lang } = useLang();
  const { add } = useCart();
  const [qty, setQty] = useState(1);
  const [size, setSize] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const product = getProduct(id);

  useEffect(() => {
    setSize(null);
    setQty(1);
    setImgIdx(0);
  }, [id]);

  if (!product) return <Navigate to="/shop" replace />;

  const seo = getProductSeo(product.id);
  const soldOut = Boolean(product.soldOut);
  const alt = product.altId ? getProduct(product.altId) : null;
  const gallery = product.gallery || [product.image];
  const related = PRODUCTS.filter((p) => p.category === product.category && p.id !== product.id).slice(0, 4);

  return (
    <div data-testid="page-pdp" className="bg-cream">
      <Seo
        title={seo.title}
        description={seo.description}
        path={seo.path}
        image={seo.image}
        type="product"
        jsonLd={[
          productLd(product),
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Shop", path: "/shop" },
            { name: product.name.de, path: `/product/${product.id}` },
          ]),
        ]}
      />
      <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-8 md:py-12">
        <Link to="/shop" className="inline-flex items-center gap-2 kicker hover:text-pink transition-colors" data-testid="pdp-back">
          <ArrowLeft className="w-4 h-4" strokeWidth={2} /> {t.pdp.back}
        </Link>

        <div className="grid lg:grid-cols-2 border border-ink mt-6">
          {/* Image column */}
          <Reveal className="border-b lg:border-b-0 lg:border-r border-ink">
            <div
              className="relative aspect-square overflow-hidden flex items-center justify-center"
              style={{ backgroundColor: product.tile || "#F9F8F6" }}
            >
              <img
                src={gallery[imgIdx]}
                alt={tr(product.name)}
                className={`w-full h-full object-cover ${soldOut ? "grayscale-[45%] opacity-80" : ""}`}
              />
              <div className="absolute top-4 right-4">
                <Stamp size={84} bg="bg-cream" />
              </div>
              <span className="absolute bottom-4 left-4 display uppercase text-2xl bg-cream border border-ink px-3 py-0.5">
                {product.number}
              </span>
              {soldOut && (
                <span className="absolute top-4 left-4 kicker bg-sand text-ink/60 border border-ink/40 px-3 py-1" data-testid="pdp-soldout-badge">
                  {t.shop.soldOut}
                </span>
              )}
            </div>
            {gallery.length > 1 && (
              <div className="flex gap-px bg-ink border-t border-ink" data-testid="pdp-gallery">
                {gallery.map((src, i) => (
                  <button
                    key={i}
                    onClick={() => setImgIdx(i)}
                    data-testid={`pdp-thumb-${i}`}
                    className={`w-20 h-20 bg-cream flex-shrink-0 overflow-hidden ${imgIdx === i ? "outline outline-2 outline-pink -outline-offset-2" : "opacity-70 hover:opacity-100"}`}
                    aria-label={`Bild ${i + 1}`}
                  >
                    <img src={src} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </Reveal>

          {/* Details column */}
          <Reveal delay={0.1} className="p-6 md:p-10">
            <p className="kicker" style={{ color: soldOut ? "#9a958c" : product.accent }}>
              {tr(product.enemy)}
            </p>
            <h1 className="display uppercase text-5xl md:text-6xl leading-[0.86] mt-2 text-brand">{tr(product.name)}</h1>
            <p className="mt-2 text-lg font-bold text-ink/60">{tr(product.claim)}</p>

            {product.packaging && (
              <p className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-ink/60">
                <Package className="w-4 h-4" strokeWidth={2} /> {t.pdp.packaging}: {tr(product.packaging)}
              </p>
            )}

            {product.sizes && (
              <div className="mt-5" data-testid="pdp-sizes">
                <p className="kicker text-ink/60 mb-2">{lang === "de" ? "Größe" : "Size"}</p>
                <div className="flex flex-wrap gap-2">
                  {product.sizes.map((sz) => {
                    const st = (product.stock && product.stock[sz]) || "in";
                    const sold = st === "sold";
                    return (
                      <button
                        key={sz}
                        onClick={() => !sold && setSize(sz)}
                        disabled={sold}
                        title={sold ? t.pdp.soldOut : st === "low" ? t.pdp.lowStock : sz}
                        data-testid={`pdp-size-${sz}`}
                        className={`relative btn w-12 h-12 text-sm ${
                          sold
                            ? "bg-sand text-ink/30 line-through border-ink/20 cursor-not-allowed"
                            : size === sz
                            ? "bg-ink text-cream"
                            : "bg-white text-ink hover:bg-ink hover:text-cream"
                        }`}
                      >
                        {sz}
                        {!sold && st === "low" && (
                          <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-orange border border-ink" />
                        )}
                      </button>
                    );
                  })}
                </div>
                {size && (
                  <p
                    className="mt-2 kicker"
                    data-testid="pdp-size-status"
                    style={{ color: product.stock && product.stock[size] === "low" ? "#FF6600" : "#00CFA8" }}
                  >
                    {product.stock && product.stock[size] === "low" ? `${size} · ${t.pdp.lowStock}` : `${size} · ✓`}
                  </p>
                )}
              </div>
            )}

            <p className="mt-5 text-lg font-medium text-ink/80">{tr(product.long)}</p>

            <div className="mt-6 flex items-center gap-4">
              <span className={`display text-5xl ${soldOut ? "line-through text-ink/40" : ""}`}>€{product.price}</span>
            </div>

            {soldOut ? (
              <div className="mt-6 border border-ink bg-sand p-5" data-testid="pdp-soldout-note">
                <p className="font-bold">{t.pdp.soldOutCta}</p>
                <p className="text-sm text-ink/60 mt-1">{t.pdp.jarNote}</p>
                {alt && !alt.soldOut && (
                  <Link to={`/product/${alt.id}`} data-testid="pdp-alt-link" className="btn-accent h-12 px-6 mt-4 text-sm">
                    {t.pdp.jarAlt} <ArrowRight className="w-4 h-4" strokeWidth={2} />
                  </Link>
                )}
              </div>
            ) : (
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <div className="flex items-center border border-ink bg-white h-14 rounded-full overflow-hidden">
                  <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-12 h-full flex items-center justify-center hover:bg-lime transition-colors" aria-label="minus">
                    <Minus className="w-4 h-4" strokeWidth={2.5} />
                  </button>
                  <span className="w-10 text-center display text-xl" data-testid="pdp-qty">{qty}</span>
                  <button onClick={() => setQty((q) => q + 1)} className="w-12 h-full flex items-center justify-center hover:bg-lime transition-colors" aria-label="plus">
                    <Plus className="w-4 h-4" strokeWidth={2.5} />
                  </button>
                </div>
                <button
                  onClick={() => {
                    if (product.sizes && !size) return;
                    const item = product.sizes
                      ? { ...product, id: `${product.id}-${size}`, name: { de: `${product.name.de} · ${size}`, en: `${product.name.en} · ${size}` } }
                      : product;
                    add(item, qty);
                  }}
                  disabled={product.sizes && !size}
                  data-testid="pdp-add-to-cart"
                  className={`h-14 px-8 text-base flex-1 min-w-[200px] ${product.sizes && !size ? "btn-disabled" : "btn-primary"}`}
                >
                  <Plus className="w-5 h-5" strokeWidth={2.5} /> {product.sizes && !size ? (lang === "de" ? "Größe wählen" : "Select size") : `${t.addToCart} — €${(product.price * qty).toFixed(0)}`}
                </button>
              </div>
            )}

            <div className="mt-5 flex items-center gap-2 text-sm font-medium text-ink/60">
              <Truck className="w-4 h-4" strokeWidth={2} /> {t.pdp.ships}
            </div>

            <div className="mt-8 card-line p-6 bg-white">
              <p className="display uppercase text-lg" style={{ color: product.accent }}>{t.pdp.benefits}</p>
              <ul className="mt-3 space-y-2.5">
                {tr(product.benefits).map((b, i) => (
                  <li key={i} className="flex items-center gap-3 font-medium">
                    <span className="w-6 h-6 rounded-full flex items-center justify-center border border-ink flex-shrink-0" style={{ backgroundColor: product.accent }}>
                      <Check className="w-3.5 h-3.5 text-ink" strokeWidth={3} />
                    </span>
                    {b}
                  </li>
                ))}
              </ul>
              <p className="kicker mt-6 text-ink/50">{t.pdp.details}</p>
              <p className="mt-1 font-bold">{tr(product.ingredients)}</p>
            </div>
          </Reveal>
        </div>

        {related.length > 0 && (
          <div className="mt-16">
            <h2 className="display uppercase text-4xl md:text-5xl mb-6 text-brand">{t.pdp.related}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-ink border border-ink">
              {related.map((p) => (
                <ProductCard key={p.id} product={p} attached />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
